import { useEffect, useRef, useState } from "react";
import Alert from "react-bootstrap/Alert";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Spinner from "react-bootstrap/Spinner";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { fetchPageContent } from "../api.js";
import Hero from "../components/Hero.jsx";
import PageSections from "../components/PageSections.jsx";
import SalesForm from "../components/SalesForm.jsx";
import SupportForm from "../components/SupportForm.jsx";
import { useAuth } from "../context/AuthContext.jsx";

export default function ContactsPage() {
  const { user, token } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formType, setFormType] = useState("sales");
  // Seed message handed off from the "Request for Quote" button. Consumed once
  // (see effect below) so it never fights the user's own typing afterward.
  const [quoteMessage, setQuoteMessage] = useState("");
  const sectionRef = useRef(null);
  const quoteConsumed = useRef(false);

  useEffect(() => {
    let cancelled = false;
    fetchPageContent("contacts")
      .then((data) => { if (!cancelled) setPage(data); })
      .catch((e) => { if (!cancelled) setError(e?.message || "Failed to load"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Quote handoff: force the Sales tab, seed the message, scroll the form into
  // view, then clear the router state so the prefill happens exactly once (a
  // refresh or back-nav gives a clean form, not a re-injected message).
  useEffect(() => {
    const quote = location.state?.quote;
    if (!quote || quoteConsumed.current) return;
    quoteConsumed.current = true;
    setFormType("sales");
    setQuoteMessage(quote.message ?? "");
    navigate(location.pathname, { replace: true, state: null });
  }, [location, navigate]);

  // Land at the top of the "Inquiries" section once content is loaded and a quote
  // was seeded. Targets the section start (not the form card) so we don't overshoot
  // past the header; the section's scroll-margin-top clears the sticky navbar.
  useEffect(() => {
    if (!loading && quoteMessage && sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [loading, quoteMessage]);

  if (loading) {
    return (
      <div className="py-5 d-flex justify-content-center">
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Loading</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <>
      <Hero hero={page?.hero} />
      <section className="pmp-contact-section" ref={sectionRef}>
        <Container>
          {/* Section header */}
          <Row className="justify-content-center text-center mb-4">
            <Col md={8} lg={6}>
              <p className="pmp-kicker mb-2">Inquiries</p>
              <h2 className="fw-bold mb-2">How can we help you?</h2>
              <p className="text-secondary mb-4">
                Choose a topic and our team will respond within one business day.
              </p>
              {/* Pill-style type selector */}
              <div className="pmp-contact-type-group">
                <button
                  className={`pmp-contact-type-btn${formType === "sales" ? " active" : ""}`}
                  onClick={() => setFormType("sales")}
                >
                  Contact Sales
                </button>
                <button
                  className={`pmp-contact-type-btn${formType === "support" ? " active" : ""}`}
                  onClick={() => setFormType("support")}
                >
                  Report an Issue
                </button>
              </div>
            </Col>
          </Row>

          {/* Form panel */}
          {formType === "sales" && (
            <Row className="justify-content-center">
              <Col md={8} lg={7}>
                <div className="pmp-contact-card">
                  <SalesForm user={user} token={token} initialMessage={quoteMessage} />
                </div>
              </Col>
            </Row>
          )}
          {formType === "support" && (
            user ? (
              <Row className="justify-content-center">
                <Col md={8} lg={7}>
                  <div className="pmp-contact-card">
                    <SupportForm user={user} token={token} />
                  </div>
                </Col>
              </Row>
            ) : (
              <Row className="justify-content-center">
                <Col md={8} lg={7}>
                  <div className="pmp-contact-card pmp-contact-card--auth text-center py-5">
                    <p className="mb-1 fw-semibold">Sign in to report an issue</p>
                    <p className="text-muted mb-3 small">
                      Support requests require an account so we can follow up with you directly.
                    </p>
                    <Link to="/login" className="btn btn-primary px-4">
                      Sign in
                    </Link>
                  </div>
                </Col>
              </Row>
            )
          )}
        </Container>
      </section>
      <PageSections sections={page?.sections} />
    </>
  );
}
